import apiClient from './client'

export const evaluationsApi = {
  // Returns PaginatedResponse — data array is at r.data.data (auto-unwrapped if sole key)
  getEvaluations: (params?: Record<string, unknown>) =>
    apiClient.get('/evaluations', { params }).then((r) => {
      // PaginatedResponse has multiple keys so interceptor won't unwrap — pull .data array
      const d = r.data
      return {
        data: Array.isArray(d) ? d : (d?.data ?? []),
        total: d?.total ?? 0,
        page: d?.page ?? 1,
        per_page: d?.per_page ?? 20,
        last_page: d?.last_page ?? 1,
      }
    }),

  getEvaluation: (id: number) =>
    apiClient.get(`/evaluations/${id}`).then((r) => r.data),

  // Returns list of completable sessions (completed sessions + past confirmed bookings)
  getCompletableSessions: () =>
    apiClient.get('/evaluations/completable-sessions').then((r) => r.data),

  createEvaluation: (data: { session_id?: number; booking_id?: number }) =>
    apiClient.post('/evaluations', data).then((r) => r.data),

  updateEvaluation: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/evaluations/${id}`, data).then((r) => r.data),

  approveEvaluation: (id: number) =>
    apiClient.post(`/evaluations/${id}/approve`).then((r) => r.data),

  generateAiSummary: (id: number) =>
    apiClient.post(`/evaluations/${id}/generate-ai-summary`).then((r) => r.data),

  getNotes: (sessionId?: number) =>
    apiClient
      .get('/notes', { params: sessionId ? { session_id: sessionId } : {} })
      .then((r) => r.data),

  createNote: (data: Record<string, unknown>) =>
    apiClient.post('/notes', data).then((r) => r.data),

  updateNote: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/notes/${id}`, data).then((r) => r.data),

  deleteNote: (id: number) =>
    apiClient.delete(`/notes/${id}`).then((r) => r.data),

  transcribeAudio: (formData: FormData) =>
    apiClient
      .post('/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data as { text: string }),

  suggestPhrasing: (text: string) =>
    apiClient
      .post('/evaluations/suggest-phrasing', { text })
      .then((r) => r.data as { suggestions: { original: string; suggested: string; reason: string }[] }),

  getFeedback: () =>
    apiClient.get('/feedback').then((r) => r.data),

  getFeedbackById: (id: number) =>
    apiClient.get(`/feedback/${id}`).then((r) => r.data),

  getMyRatings: () =>
    apiClient.get('/ratings/me').then((r) => r.data.data ?? r.data),

  getRecordings: () =>
    apiClient.get('/interviewer/recordings').then((r) => r.data),

  getQuestionSet: (sessionId: number) =>
    apiClient
      .get(`/question-sets/session/${sessionId}`)
      .then((r) => r.data.data ?? r.data),

  generateQuestions: (sessionId: number) =>
    apiClient
      .post(`/interviewer/sessions/${sessionId}/generate-questions`, {})
      .then((r) => r.data.data ?? r.data),

  // Client-facing: averaged per-question-type scores
  getClientPerformanceOverview: (evaluationId: number) =>
    apiClient
      .get(`/feedback/${evaluationId}/performance-overview`)
      .then((r) => r.data.data ?? r.data),

  // Client-facing: per-question scores, notes, and recommendations (delivered only)
  getClientQuestionBundle: (evaluationId: number) =>
    apiClient
      .get(`/feedback/${evaluationId}/questions`)
      .then((r) => r.data.data ?? r.data),

  // ── Per-Question Human Evaluation ───────────────────────────────────────
  getQuestionEvaluationBundle: (evaluationId: number) =>
    apiClient
      .get(`/evaluations/${evaluationId}/questions`)
      .then((r) => r.data.data ?? r.data),

  upsertQuestionResponse: (
    evaluationId: number,
    questionIndex: number,
    data: {
      scores?: Record<string, number>
      note?: string | null
      recommendation?: string | null
    },
  ) =>
    apiClient
      .put(`/evaluations/${evaluationId}/questions/${questionIndex}`, data)
      .then((r) => r.data.data ?? r.data),
}
